// Comments Service
import { Api } from '@infinitro/global';
import { AsyncGetCommentReplies, AsyncGetGameComments, CreateComment, DeleteComment, ToggleCommentLike, UpdateComment } from './api';
// Flag to switch between mock and real API
const USE_MOCK = false;
// ============================================================================
// MOCK IMPLEMENTATIONS (for testing)
// ============================================================================
const mockComments = [
    {
        commentId: '1',
        playerId: 'player1',
        username: 'Buddy20',
        parentCommentId: undefined,
        commentText: 'This game is absolutely amazing! Got into the bonus round and won big. The graphics are stunning and the gameplay is super smooth!',
        likeCount: 21,
        replyCount: 3,
        createdAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
        updatedAt: undefined
    },
    {
        commentId: '1-1',
        playerId: 'player2',
        username: 'jchikas',
        parentCommentId: '1',
        commentText: 'I totally agree! The bonus features are really generous and exciting.',
        likeCount: 14,
        replyCount: 0,
        createdAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
        updatedAt: undefined
    },
    {
        commentId: '1-2',
        playerId: 'player2',
        username: 'jchikas',
        parentCommentId: '1',
        commentText: 'Best game I played this month!',
        likeCount: 8,
        replyCount: 0,
        createdAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
        updatedAt: undefined
    },
    {
        commentId: '1-3',
        playerId: 'player3',
        username: 'Simonebc',
        parentCommentId: '1',
        commentText: 'The free spins round is incredibly rewarding. Highly recommend this game!',
        likeCount: 12,
        replyCount: 0,
        createdAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
        updatedAt: undefined
    },
    {
        commentId: '2',
        playerId: 'player4',
        username: 'GameMaster',
        parentCommentId: undefined,
        commentText: 'One of the best slots I have ever played. The multipliers are fantastic and the RTP is very fair. Keep coming back to this one!',
        likeCount: 35,
        replyCount: 1,
        createdAt: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000).toISOString(),
        updatedAt: undefined
    },
    {
        commentId: '2-1',
        playerId: 'player5',
        username: 'LuckyPlayer88',
        parentCommentId: '2',
        commentText: "Couldn't agree more! The win potential is amazing and it's so much fun to play.",
        likeCount: 18,
        replyCount: 0,
        createdAt: new Date(Date.now() - 11 * 24 * 60 * 60 * 1000).toISOString(),
        updatedAt: undefined
    }
];
async function mockCreateComment(input) {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 500));
    return {
        success: true,
        commentId: `comment-${Date.now()}`,
        message: 'Comment created successfully'
    };
}
async function mockUpdateComment(input) {
    await new Promise((resolve) => setTimeout(resolve, 500));
    return {
        success: true,
        commentId: input.commentId,
        message: 'Comment updated successfully'
    };
}
async function mockDeleteComment(commentId) {
    await new Promise((resolve) => setTimeout(resolve, 300));
    return {
        success: true,
        message: 'Comment deleted successfully'
    };
}
async function mockGetGameComments(gameSlug, providerSlug, limit, sortBy) {
    await new Promise((resolve) => setTimeout(resolve, 300));
    // Return only top-level comments (no parentCommentId)
    const topLevelComments = mockComments.filter((c) => !c.parentCommentId);
    return topLevelComments.slice(0, limit);
}
async function mockGetCommentReplies(commentId) {
    await new Promise((resolve) => setTimeout(resolve, 300));
    // Return comments with matching parentCommentId
    return mockComments.filter((c) => c.parentCommentId === commentId);
}
async function mockToggleCommentLike(commentId) {
    await new Promise((resolve) => setTimeout(resolve, 300));
    // Randomly toggle for demo purposes
    const liked = Math.random() > 0.5;
    return {
        success: true,
        liked
    };
}
// ============================================================================
// API IMPLEMENTATIONS
// ============================================================================
async function apiCreateComment(input) {
    const result = await CreateComment({
        variables: input,
        context: { api: Api.player } // Requires authentication
    });
    if (!result.data?.createComment) {
        throw new Error('Failed to create comment');
    }
    return {
        success: result.data.createComment.success,
        commentId: result.data.createComment.commentId || '',
        message: result.data.createComment.message || ''
    };
}
async function apiUpdateComment(input) {
    const result = await UpdateComment({
        variables: input,
        context: { api: Api.player } // Requires authentication
    });
    if (!result.data?.updateComment) {
        throw new Error('Failed to update comment');
    }
    return {
        success: result.data.updateComment.success,
        commentId: result.data.updateComment.commentId || undefined,
        message: result.data.updateComment.message || ''
    };
}
async function apiDeleteComment(commentId) {
    const result = await DeleteComment({
        variables: { commentId },
        context: { api: Api.player } // Requires authentication
    });
    if (!result.data?.deleteComment) {
        throw new Error('Failed to delete comment');
    }
    return {
        success: result.data.deleteComment.success,
        message: result.data.deleteComment.message || ''
    };
}
async function apiGetGameComments(gameSlug, providerSlug, limit, sortBy) {
    const result = await AsyncGetGameComments({
        variables: { gameSlug, providerSlug, limit, sortBy },
        context: { api: Api.public },
        fetchPolicy: 'network-only' // Always fetch fresh data from server
    });
    if (!result.data?.getGameComments) {
        return [];
    }
    return result.data.getGameComments.map((comment) => ({
        commentId: comment.commentId,
        playerId: comment.playerId,
        username: comment.username || '',
        parentCommentId: comment.parentCommentId || undefined,
        commentText: comment.commentText,
        likeCount: comment.likeCount,
        replyCount: comment.replyCount,
        createdAt: comment.createdAt,
        updatedAt: comment.updatedAt || undefined
    }));
}
async function apiGetCommentReplies(commentId) {
    const result = await AsyncGetCommentReplies({
        variables: { commentId },
        context: { api: Api.public }, // Public endpoint
        fetchPolicy: 'network-only' // Always fetch fresh data from server, no caching
    });
    if (!result.data?.getCommentReplies) {
        return [];
    }
    return result.data.getCommentReplies.map((comment) => ({
        commentId: comment.commentId,
        playerId: comment.playerId,
        username: comment.username || '',
        parentCommentId: comment.parentCommentId || undefined,
        commentText: comment.commentText,
        likeCount: comment.likeCount,
        replyCount: comment.replyCount,
        createdAt: comment.createdAt,
        updatedAt: comment.updatedAt || undefined
    }));
}
async function apiToggleCommentLike(commentId) {
    const result = await ToggleCommentLike({
        variables: { commentId },
        context: { api: Api.player } // Requires authentication
    });
    if (!result.data?.toggleCommentLike) {
        throw new Error('Failed to toggle comment like');
    }
    return {
        success: result.data.toggleCommentLike.success,
        liked: result.data.toggleCommentLike.liked
    };
}
// ============================================================================
// EXPORTED API
// ============================================================================
export async function createComment(input) {
    if (USE_MOCK) {
        return mockCreateComment(input);
    }
    return apiCreateComment(input);
}
export async function updateComment(input) {
    if (USE_MOCK) {
        return mockUpdateComment(input);
    }
    return apiUpdateComment(input);
}
export async function deleteComment(commentId) {
    if (USE_MOCK) {
        return mockDeleteComment(commentId);
    }
    return apiDeleteComment(commentId);
}
export async function getGameComments(gameSlug, providerSlug, limit, sortBy) {
    if (USE_MOCK) {
        return mockGetGameComments(gameSlug, providerSlug, limit, sortBy);
    }
    return apiGetGameComments(gameSlug, providerSlug, limit, sortBy);
}
export async function getCommentReplies(commentId) {
    if (USE_MOCK) {
        return mockGetCommentReplies(commentId);
    }
    return apiGetCommentReplies(commentId);
}
export async function toggleCommentLike(commentId) {
    if (USE_MOCK) {
        return mockToggleCommentLike(commentId);
    }
    return apiToggleCommentLike(commentId);
}
